import { useAppSelector } from "hooks/storeHooks"
import { useLocation, Link as RouterLink } from "react-router-dom"
import { ScanFace, Loader2 } from "lucide-react"
import { type INavSectionsItems, ProductCategoriesLink } from "./types"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface NavSectionCategoriesProps {
  collapsed?: boolean
}

export const NavSectionCategories = ({ collapsed = false }: NavSectionCategoriesProps) => {
  const { t } = useTranslation()
  const items = [
    {
      to: ProductCategoriesLink.MAKEUP,
      activePaths: [ProductCategoriesLink.MAKEUP, ProductCategoriesLink.GLASSES],
      label: t("nav.products"),
      icon: <ScanFace className="h-4 w-4" />,
      isMain: true,
    },
  ]
  const location = useLocation()
  const productsCategories = useAppSelector((state) => state.products.productsCategories)
  const productsCategoriesLoading = useAppSelector(
    (state) => state.products.productsCategoriesLoading
  )
  const comingSoonCategories = [
    t("productCategories.hairDye"),
    t("productCategories.lenses"),
    t("productCategories.skinCare"),
  ]
  const currentCategoryPath = `/${location.pathname.split("/").filter(Boolean)[0] || ""}`
  const productsListIndex =
    productsCategories?.findIndex((el) => el.to === currentCategoryPath) ?? -1

  const getLinkTo = (link: INavSectionsItems): string => {
    if (link.isMain && productsCategories && productsCategories.length > 0) {
      const found = productsCategories.find((cat) => cat.to === link.to)
      return found ? link.to : productsCategories[0].to
    }
    return link.to
  }

  if (collapsed)
    return (
      <>
        {items.map((link) => (
          <Tooltip key={link.to}>
            <TooltipTrigger asChild>
              <RouterLink
                to={getLinkTo(link)}
                data-testid="nav-products"
                className={cn(
                  "flex items-center justify-center rounded-md py-2 transition-colors",
                  productsListIndex >= 0
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                {link.icon}
              </RouterLink>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              {link.label}
            </TooltipContent>
          </Tooltip>
        ))}
      </>
    )

  return (
    <>
      {items.map((link) => (
        <RouterLink
          key={link.to}
          to={getLinkTo(link)}
          data-testid="nav-products"
          className={cn(
            "group flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] transition-colors",
            productsListIndex >= 0
              ? "bg-accent text-foreground font-semibold"
              : "font-medium text-foreground/90 hover:bg-accent hover:text-foreground"
          )}
        >
          <span className="opacity-80 group-hover:opacity-100">{link.icon}</span>
          <span className="truncate">{link.label}</span>
        </RouterLink>
      ))}

      {productsCategoriesLoading ? (
        <div className="flex items-center justify-center py-2 pl-8">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground/50" />
        </div>
      ) : (
        productsCategories?.map(({ to, label }, ind) => (
          <RouterLink
            key={label + to + ind}
            to={to}
            data-testid={`nav-category-${to.replace(/^\//, "")}`}
            className={cn(
              "flex items-center gap-2 rounded-md py-[6px] pl-9 pr-2 text-[13px] transition-colors",
              currentCategoryPath === to
                ? "text-foreground font-semibold"
                : "font-medium text-foreground/90 hover:text-foreground"
            )}
          >
            <span
              className={cn(
                "h-1 w-1 rounded-full",
                currentCategoryPath === to ? "bg-foreground" : "bg-foreground/55"
              )}
            />
            <span className="truncate">{label}</span>
          </RouterLink>
        ))
      )}

      {!productsCategoriesLoading &&
        comingSoonCategories.map((label) => (
          <div
            key={label}
            data-testid="nav-category-coming-soon"
            className="flex cursor-default items-center rounded-md py-[6px] pl-9 pr-2 text-[13px] text-foreground/55"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-1 w-1 shrink-0 rounded-full bg-foreground/35" />
                  <span className="truncate">{label}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {t("nav.comingSoon")}
              </TooltipContent>
            </Tooltip>
          </div>
        ))}
    </>
  )
}
